'use client';
/**
 * TOUT MOMENT DE FLOTTEMENT ALLUME LE TÉMOIN — lectures comprises.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * ── Ce qui manquait (RQ, 26/08/2026) ────────────────────────────────────────
 *
 * « Je veux qu'il apparaisse à chaque fois qu'il y a un moment de latence pour
 * enregistrer OU OUVRIR UNE TABLE, ou tout autre moment de flottement. »
 *
 * Le témoin ne connaissait que les ÉCRITURES : `smq`/`smqData` d'abord, puis
 * `fetchEcriture` (le 25/08, pour rattraper 86 appels directs). Les LECTURES
 * n'allumaient rien. Or c'est là que l'attente est la plus visible : ouvrir le
 * registre des examens, déplier un dossier, charger la vue à plat et ses 61
 * colonnes — plusieurs centaines de millisecondes pendant lesquelles l'écran
 * reste tel quel, et l'on reclique.
 *
 * ── Pourquoi un ENROBAGE GLOBAL de `fetch`, cette fois ──────────────────────
 *
 * Le 25/08, la réponse a été un enrobage explicite (`fetchEcriture`) appliqué
 * appel par appel. Elle était juste pour les écritures — il y en a un nombre
 * fini et elles se repèrent. Elle ne l'est pas pour les lectures : il y en a
 * partout, il s'en ajoute à chaque écran, et la liste serait à refaire
 * indéfiniment. Le commentaire de `ecrire.ts` note lui-même que la première
 * passe avait laissé 86 appels de côté — c'est le symptôme d'une méthode qui
 * ne tient pas à cette échelle.
 *
 * L'enrobage global attrape TOUT, y compris le code écrit demain.
 *
 * ⚠️ TROIS PRÉCAUTIONS, et elles sont la raison d'être de ce fichier :
 *
 *  1. SEULES NOS ADRESSES sont comptées (`/api/…`, même origine). Une requête
 *     vers un tiers — un service de conversion, une police — n'a rien à voir
 *     avec l'attente de l'utilisateur devant SON action.
 *
 *  2. LE COMPTEUR DESCEND DANS UN `finally`. Sur le chemin d'erreur comme sur
 *     celui du succès : un témoin resté allumé après une coupure réseau ferait
 *     croire que l'application travaille, et l'on attendrait devant un écran
 *     mort. Même contrat que `smq` et `fetchEcriture`.
 *
 *  3. LA RÉPONSE N'EST JAMAIS TOUCHÉE. On rend l'objet `Response` tel quel, non
 *     lu, non cloné : le lire ici consommerait le flux et l'appelant recevrait
 *     un corps vide — une panne silencieuse, et générale.
 *
 * ⚠️ L'INSTALLATION EST IDEMPOTENTE. React monte deux fois en mode strict, et
 * le rechargement à chaud rejoue le module : sans le drapeau, `fetch`
 * s'enroberait lui-même en cascade et chaque requête compterait double — le
 * témoin ne s'éteindrait plus jamais.
 */

let enCours = 0;
const abonnes = new Set<(n: number) => void>();

function diffuser() {
  for (const f of abonnes) f(enCours);
}

/** S'abonner au nombre de requêtes en vol. Rend la fonction de désabonnement. */
export function surAttente(f: (n: number) => void): () => void {
  abonnes.add(f);
  f(enCours);
  return () => { abonnes.delete(f); };
}

/** Nos adresses, et elles seules. Une URL relative commençant par `/api/` ou
 *  une absolue de MÊME origine — les deux formes existent dans le code. */
function nousConcerne(entree: RequestInfo | URL): boolean {
  try {
    const brut = typeof entree === 'string' ? entree
      : entree instanceof URL ? entree.href
        : (entree as Request).url;
    if (!brut) return false;
    const chemin = brut.startsWith('/api/') ? brut
      : (typeof window !== 'undefined' && brut.startsWith(window.location.origin))
        ? new URL(brut).pathname
        : '';
    if (!chemin.startsWith('/api/')) return false;
    return !LONGUES.some((p) => chemin.startsWith(p));
  } catch {
    return false;
  }
}

/**
 * Les appels LONGS PAR NATURE, exclus du témoin.
 *
 * L'entretien de déclaration NC (Claude) peut prendre jusqu'à 45 secondes. Le
 * témoin est au CENTRE de l'écran : le laisser tourner si longtemps pendant
 * qu'on continue de travailler ailleurs ne dit plus « patientez », il dit
 * « quelque chose ne va pas ». Ces écrans-là ont leur propre indicateur, à
 * l'endroit où la réponse arrivera.
 */
const LONGUES = ['/api/ged/ia/'];

const MARQUE = '__bio24AttenteInstallee';

/** Enrobe `window.fetch` une fois pour toutes. Sans effet côté serveur. */
export function installerTemoinAttente(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as Record<string, unknown>;
  if (w[MARQUE]) return;
  w[MARQUE] = true;

  const origine = window.fetch.bind(window);
  window.fetch = async (entree: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!nousConcerne(entree)) return origine(entree, init);
    enCours += 1;
    diffuser();
    try {
      return await origine(entree, init);
    } finally {
      enCours = Math.max(0, enCours - 1);
      diffuser();
    }
  };
}
